// model report list component, used by both admin and customer views. The admin view has extra columns and actions, and is read-only for customers. The list supports searching, filtering, paging, and deep linking to a specific report by ID.
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/Grey Imports Logo.png';
import roverLinkIcon from '../assets/g2.png';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PlusIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import AdminLayout from './AdminLayout';
import CustomerLayout from './CustomerLayout';
import { usePricing } from './usePricing';

// Regex to match SEV-#### tokens in a string, case-insensitive and global.
const SEV_TOKEN_PATTERN = /(SEV-\d+)/gi;
const MELBOURNE_TIMEZONE = 'Australia/Melbourne';

// Strips Tailwind text-color utilities from a className string so they don't
// fight a component's own built-in text color (e.g. the blue Rover link color).
// Sizing / whitespace / layout classes (text-[13px], md:text-sm, break-words, etc.)
// are left untouched.
const stripTextColorClasses = (className = '') =>
  className
    .split(' ')
    .filter(Boolean)
    .filter((cls) => !/^text-(inherit|current|transparent|black|white|slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)(-\d+)?$/.test(cls))
    .join(' ');

// Renders a Rover link with an icon, or plain text if no URL is provided. The link opens in a new tab and stops propagation to avoid triggering row clicks.
const RoverApprovalLink = ({ value, roverUrl, className = '', compact = false }) => {
  const href = roverUrl?.trim();

  if (!href) {
    return <span className={className}>{value || '-'}</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      title="Click to view more details - Rover"
      className={`font-medium text-blue-700 underline decoration-blue-500 underline-offset-2 hover:text-blue-800 ${className}`.trim()}
    >
      <img
        src={roverLinkIcon}
        alt=""
        aria-hidden="true"
        className={`${compact ? 'mr-1 inline-block h-4 w-4 align-text-bottom object-contain' : 'mr-1 inline-block h-6 w-6 align-middle object-contain'}`}
      />
      {value}
    </a>
  );
};

// Renders plain text for a Rover approval, or a dash if no value is provided.
const RoverApprovalText = ({ value, className = '' }) => (
  <span className={className}>{value || '-'}</span>
);

// Splits a SEVS entry string into plain text plus linked SEV-#### tokens.
const RoverSevLinks = ({ value, roverUrls, className = '', compact = false }) => {
  const text = value || '-';
  const matches = [...text.matchAll(SEV_TOKEN_PATTERN)];

  if (matches.length === 0) {
    return <span className={className}>{text}</span>;
  }

  const urls = String(roverUrls || '').split('|').map((item) => item.trim()).filter(Boolean);
  const parts = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    const token = match[0];
    const startIndex = match.index ?? 0;
    const endIndex = startIndex + token.length;

    if (startIndex > lastIndex) {
      parts.push(
        <span key={`text-${index}-${startIndex}`} className={className}>
          {text.slice(lastIndex, startIndex)}
        </span>
      );
    }

    const roverUrl = urls[index] || null;
    parts.push(
      roverUrl ? (
        <RoverApprovalLink
          key={`link-${token}-${index}`}
          value={token}
          roverUrl={roverUrl}
          compact={compact}
          className={stripTextColorClasses(className)}
        />
      ) : (
        <span key={`plain-${token}-${index}`} className={className}>
          {token}
        </span>
      )
    );

    lastIndex = endIndex;
  });

  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-tail-${lastIndex}`} className={className}>
        {text.slice(lastIndex)}
      </span>
    );
  }

  return <>{parts}</>;
};

const getMelbourneToday = () => {
  const melbourneDate = new Intl.DateTimeFormat('en-AU', {
    timeZone: MELBOURNE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());

  const [day, month, year] = melbourneDate.split('/').map(Number);
  return new Date(year, month - 1, day);
};

const parseSevsExpiryDate = (sevsExpiry) => {
  if (!sevsExpiry) return null;

  const [day, month, year] = sevsExpiry.split('/').map(Number);
  if (!day || !month || !year) return null;

  return new Date(year, month - 1, day);
};

// Supabase Initialization
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const supabase = createClient(supabaseUrl, supabasePublishableKey);

const ModelReportDatabase = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get ID from URL params
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editReport, setEditReport] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editError, setEditError] = useState(null);
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingVariationNotification, setSendingVariationNotification] = useState(false);
  const [variationReminderOpen, setVariationReminderOpen] = useState(false);
  const [variationConfirmationOpen, setVariationConfirmationOpen] = useState(false);
  const [variationDescription, setVariationDescription] = useState('');
  const [editConfirmation, setEditConfirmation] = useState(null);
  const [editDocuments, setEditDocuments] = useState({ avv: [], mr: [] });
  const [loadingEditDocuments, setLoadingEditDocuments] = useState(false);
  const [editDocumentError, setEditDocumentError] = useState(null);
  const [uploadingDocumentType, setUploadingDocumentType] = useState(null);
  const [deletingDocumentPath, setDeletingDocumentPath] = useState(null);
  const [deletingAllDocumentType, setDeletingAllDocumentType] = useState(null);
  const [isBelow1440px, setIsBelow1440px] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1439px)').matches;
  });
  const [isBelow1280px, setIsBelow1280px] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1279px)').matches;
  });

  const { user, userRole } = useAuth();
  const { getTotalPrice, hasCustomerPrice } = usePricing(user, supabase);

  const fromPath = location.state?.from;
  const isAdminListView =
    mode === 'admin' ||
    location.pathname === '/admin/model-reports' ||
    location.pathname.startsWith('/admin/model-reports/') ||
    fromPath === '/admin' ||
    (typeof fromPath === 'string' && fromPath.startsWith('/admin/'));
  const isOwnerAnalysisView = userRole === 'owner' && location.state?.from === '/view-analysis';
  const isManagementListView = isAdminListView || isOwnerAnalysisView;
  const isPopupSelectionMode =
    typeof window !== 'undefined' && Boolean(window.opener) && !window.opener.closed;
  const isReadOnlyView = isManagementListView || ['/view-analysis', '/sevs-expiry'].includes(location.state?.from);
  const isCompactAdminTable = isManagementListView && isBelow1440px;
  const isNarrowAdminTable = isManagementListView && isBelow1280px;
  const isCompactCustomerTable = !isManagementListView && isBelow1280px;
  const hideTypeInMainTable = isCompactAdminTable || isCompactCustomerTable;
  const hideSevsMreInMainTable = isNarrowAdminTable || isCompactCustomerTable;
  const hidePrice = isReadOnlyView;
  const showPriceColumn = isManagementListView || (!hidePrice && hasCustomerPrice);
  const actionColumnVisible = !isReadOnlyView || isManagementListView;
  const expandedDetailsColSpan =
    1 +
    2 +
    (!hideSevsMreInMainTable ? 2 : 0) +
    (!hideTypeInMainTable ? 1 : 0) +
    (showPriceColumn && !isCompactAdminTable ? 1 : 0) +
    (actionColumnVisible ? 1 : 0);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [pendingSelection, setPendingSelection] = useState(null);
  const rowRefs = useRef({});
  const avvDocumentInputRef = useRef(null);
  const mrDocumentInputRef = useRef(null);



  const searchFields = [
    { value: 'make', label: 'Make' },
    { value: 'model', label: 'Model' },
    { value: 'sevs_entry', label: 'SEVS Entry' },
    { value: 'mr_approval', label: 'MRE Approval' },
    { value: 'chassis_code', label: 'Chassis Code' },
    { value: 'body_type', label: 'Body Type' }
  ];

  const vehicleTypeOptions = [
    'Common',
    'Fixed Price',
    'Bikes',
    'Unique',
    'Trucks',
    'Welcab Ramp',
    'Welcab Side Seat',
    'Campervan'
  ];

  const editableModelReportFields = [
    { name: 'make', label: 'Make' },
    { name: 'model', label: 'Model' },
    { name: 'chassis_code', label: 'Chassis Code' },
    { name: 'body_type', label: 'Body Type' },
    { name: 'engine_option_1', label: 'Engine Option 1' },
    { name: 'engine_option_2', label: 'Engine Option 2' },
    { name: 'transmission', label: 'Transmission' },
    { name: 'drivetrain', label: 'Drivetrain' },
    { name: 'seats_option_1', label: 'Seats Option 1' },
    { name: 'seats_option_2', label: 'Seats Option 2' },
    { name: 'seat_option_3', label: 'Seat Option 3' },
    { name: 'sevs_entry', label: 'SEVS Entry' },
    { name: 'welcab_type', label: 'Welcab Type' },
    { name: 'mr_approval', label: 'MRE Approval' },
    { name: 'mr_wi_reference', label: 'MR WI Reference' },
    { name: 'mr_vc_reference', label: 'MR VC Reference' },
    { name: 'origin_requirements', label: 'Origin Requirements' },
    { name: 'cats_or_km_required', label: 'Cats or KM' },
    { name: 'camper_conversion_required', label: 'Camper Conversion' },
    { name: 'sevs_expiry', label: 'SEVS Expiry' },
    { name: 'start_date', label: 'Start Date' },
    { name: 'end_date', label: 'End Date' },
    { name: 'gm_reference', label: 'GM Reference' },
    { name: 'cin_reference', label: 'CIN Reference' },
    { name: 'mr_variant', label: 'MR Variant' },
    { name: 'vehicle_type', label: 'Vehicle Type', type: 'select', options: vehicleTypeOptions },
    { name: 'vehicle_price', label: 'Vehicle Price', type: 'number' },
    { name: 'mr_description', label: 'MRE Description (Optional)', type: 'textarea' }
  ];

  const getEditFieldInputClassName = (fieldName, { isTextarea = false } = {}) => {
    const hasError = Boolean(editFieldErrors[fieldName]);
    const baseClass = isTextarea
      ? 'block w-full rounded-[1rem] border px-3 py-2.5 text-xs text-slate-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-100 md:px-4 md:py-3 md:text-sm'
      : 'block w-full rounded-[1rem] border px-3 py-2.5 text-xs text-slate-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-100 md:px-4 md:py-3 md:text-sm';

    if (hasError) {
      return `${baseClass} border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-100`;
    }

    return `${baseClass} border-slate-200 bg-white focus:border-blue-500`;
  };

  // Every editable field is mandatory except the MRE description; vehicle price must additionally
  // be a non-negative number.
  const validateEditForm = () => {
    const newErrors = {};

    editableModelReportFields.forEach((field) => {
      if (field.name === 'mr_description') return;

      const value = editFormData[field.name];

      if (field.name === 'vehicle_price') {
        if (value === '' || value === null || value === undefined) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (Number.isNaN(Number(value))) {
          newErrors[field.name] = 'Vehicle price must be a valid number';
        } else if (Number(value) < 0) {
          newErrors[field.name] = 'Vehicle price cannot be negative';
        }
        return;
      }

      const stringValue = value === null || value === undefined ? '' : String(value).trim();
      if (stringValue === '') {
        newErrors[field.name] = `${field.label} is required`;
      }
    });

    setEditFieldErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  };

  const scrollToFirstEditFieldError = (fieldErrors) => {
    const firstInvalidField = editableModelReportFields.find(field => fieldErrors[field.name])?.name;
    if (!firstInvalidField) return;

    requestAnimationFrame(() => {
      const fieldElement = document.querySelector(`[name="${firstInvalidField}"]`);
      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldElement?.focus?.();
    });
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery1440 = window.matchMedia('(max-width: 1439px)');
    const mediaQuery1280 = window.matchMedia('(max-width: 1279px)');
    const handleViewportChange = (event) => {
      setIsBelow1440px(mediaQuery1440.matches);
      setIsBelow1280px(mediaQuery1280.matches);
    };

    setIsBelow1440px(mediaQuery1440.matches);
    setIsBelow1280px(mediaQuery1280.matches);

    if (typeof mediaQuery1440.addEventListener === 'function') {
      mediaQuery1440.addEventListener('change', handleViewportChange);
      mediaQuery1280.addEventListener('change', handleViewportChange);
      return () => {
        mediaQuery1440.removeEventListener('change', handleViewportChange);
        mediaQuery1280.removeEventListener('change', handleViewportChange);
      };
    }

    mediaQuery1440.addListener(handleViewportChange);
    mediaQuery1280.addListener(handleViewportChange);
    return () => {
      mediaQuery1440.removeListener(handleViewportChange);
      mediaQuery1280.removeListener(handleViewportChange);
    };
  }, []);

  useEffect(() => {
    // Apply filters whenever search term or selected field changes
    const filtered = reports.filter(report => {
      const normalizedSearchTerm = searchTerm.toLowerCase();
      const fieldValue = selectedField ? report[selectedField]?.toLowerCase() || '' : '';
      const matchesSearch = !selectedField || !normalizedSearchTerm
        ? true
        : fieldValue.includes(normalizedSearchTerm);
      // If the availability filter is set to 'unavailable', only include reports that are not available to buy. Otherwise, include all reports.
      const matchesAvailability = availabilityFilter === 'unavailable'
        ? !isAvailableToBuy(report)
        : true;

      return matchesSearch && matchesAvailability;
    });
    setFilteredReports(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, selectedField, availabilityFilter, reports]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredReports.length / reportsPerPage));
  }, [filteredReports, reportsPerPage]);

  useEffect(() => {
    if (id && reports.length > 0) {
      // Find the report with matching ID
      const targetReport = reports.find(report => report.id.toString() === id);
      if (targetReport) {
        // Calculate which page the report is on
        const reportIndex = reports.indexOf(targetReport);
        const targetPage = Math.floor(reportIndex / reportsPerPage) + 1;
        setCurrentPage(targetPage);

        // Expand the row
        setExpandedRows(new Set([targetReport.id]));

        // Scroll to the row after a short delay to ensure rendering
        setTimeout(() => {
          if (rowRefs.current[targetReport.id]) {
            rowRefs.current[targetReport.id].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }
  }, [id, reports, reportsPerPage]);

  // Fetches all model reports from Supabase, applying filters for availability and sorting by MRE Approval number in descending order. The results are stored in the `reports` state variable.
  const fetchReports = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('customer_model_reports')
        .select(`
          id,
          make,
          model,
          chassis_code,
          body_type,
          engine_option_1,
          engine_option_2,
          transmission,
          drivetrain,
          seats_option_1,
          seats_option_2,
          seat_option_3,
          sevs_entry,
          welcab_type,
          mr_approval,
          mr_wi_reference,
          mr_vc_reference,
          origin_requirements,
          cats_or_km_required,
          camper_conversion_required,
          sevs_expiry,
          start_date,
          end_date,
          gm_reference,
          cin_reference,
          mr_variant,
          vehicle_type,
          vehicle_price,
          mr_description,
          is_available_to_buy,
          mr_approval_rover_url,
          sevs_entry_rover_url,
          sev_under_review,
          storage_path
        `)
        // Rows without a storage_path have no document folder yet, so they are not sellable.
        .neq('storage_path', null);

      // Customers only ever see reports flagged as available; admins see everything.
      if (!isManagementListView) {
        query = query.eq('is_available_to_buy', true);
      }

      const { data, error } = await query.order('id', { ascending: true });

      if (error) throw error;

      // Sort the data by MRE Approval number in descending order
      const sortedData = (data || []).sort((a, b) => {
        // Approval codes look like "MRE-1234"; missing or non-numeric codes sort to the bottom.
        const numA = parseInt(a.mr_approval?.replace('MRE-', '') || '0');
        const numB = parseInt(b.mr_approval?.replace('MRE-', '') || '0');
        return numB - numA;  // Descending order
      });

      setReports(sortedData);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handles the back button click, navigating to the appropriate previous page based on the context (SEVS expiry, admin list view, or dashboard).
  const handleBack = () => {
    if (fromPath === '/sevs-expiry') {
      navigate('/sevs-expiry');
      return;
    }
    if (isAdminListView) {
      const adminReturnPath =
        typeof fromPath === 'string' && fromPath.startsWith('/admin') ? fromPath : '/admin';
      navigate(adminReturnPath);
      return;
    }
    navigate(location.state?.from || '/dashboard');
  };

  // Function to get the button text based on return path
  const getBackButtonText = () => {
    if (typeof fromPath === 'string' && fromPath.startsWith('/admin/variation-history')) {
      return 'Back to Variation History';
    }
    if (fromPath === '/sevs-expiry') return 'Back to SEVS Expiry';
    if (isAdminListView) return 'Back to Admin';
    if (location.state?.from === '/view-analysis') return 'Back to Analysis';
    return 'Back to Dashboard';
  };

  const openVariationHistory = (reportId) => {
    navigate(`/admin/variation-history?mr_id=${reportId}`);
  };

  const toggleRow = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Helper function to check if a value should be displayed
  const isValidValue = (value) => {
    return value && value !== '-' && value.toString().trim() !== '';
  };

  const isExpiredSevs = (sevsExpiry) => {
    const expiryDate = parseSevsExpiryDate(sevsExpiry);
    if (!expiryDate) return false;

    return expiryDate < getMelbourneToday();
  };

  // A SEVS entry counts as "expiring soon" once it is inside the final three months before expiry.
  const isSevsExpiringSoon = (sevsExpiry) => {
    if (!sevsExpiry || isExpiredSevs(sevsExpiry)) return false;

    const expiryDate = parseSevsExpiryDate(sevsExpiry);
    if (!expiryDate) return false;

    const expiringSoonDate = new Date(expiryDate);
    expiringSoonDate.setMonth(expiringSoonDate.getMonth() - 3);

    return getMelbourneToday() >= expiringSoonDate;
  };

  const isSevsUnderReview = (report) => Boolean(report?.sev_under_review);

  // Returns the stored price for a report, prioritizing vehicle_price over price, and defaults to '-' if neither is available.
  const getStoredPrice = (report) => {
    const storedPrice = report.vehicle_price ?? report.price;
    return storedPrice ?? '-';
  };

  const getModelReportDisplayName = (report) => {
    if (!report) return '';

    const carDetails = [report.make, report.model].filter(Boolean).join(' ');

    return [report.mr_approval, carDetails].filter(Boolean).join(' ');
  };

  // Fields shown to the admin for review before the variation email goes out to customers.
  const getVariationNotificationPreview = (report) => ([
    { label: 'MRE Approval', value: report?.mr_approval },
    { label: 'Make', value: report?.make },
    { label: 'Model', value: report?.model },
    { label: 'WI Reference', value: report?.mr_wi_reference },
    { label: 'VC Reference', value: report?.mr_vc_reference }
  ]);

  const isAvailableToBuy = (report) => report?.is_available_to_buy !== false;

  const getEditFormDataFromReport = (report) => editableModelReportFields.reduce((values, field) => ({
    ...values,
    [field.name]: report?.[field.name] ?? ''
  }), {
    is_available_to_buy: report?.is_available_to_buy !== false
  });

  const normalizeEditFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') return '';

    if (field.name === 'vehicle_price') {
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? String(value) : String(numericValue);
    }

    if (field.name === 'is_available_to_buy') {
      return value === false ? 'false' : 'true';
    }

    return String(value);
  };

  const hasUnsavedEditChanges = () => {
    if (!editReport) return false;

    const comparableFields = [
      ...editableModelReportFields,
      { name: 'is_available_to_buy' }
    ];

    return comparableFields.some(field => (
      normalizeEditFieldValue(field, editFormData[field.name]) !== normalizeEditFieldValue(field, editReport[field.name])
    ));
  };

  const getDocumentFolder = (type) => (type === 'avv' ? 'AVV DOCUMENTS' : 'RAW DOCUMENTS');

  const sanitizeStorageFileName = (fileName) => {
    const normalizedName = fileName
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2010-\u2015]/g, '-')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[^a-zA-Z0-9._()\-\s]/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/-+/g, '-')
      .trim();

    return normalizedName || `document-${Date.now()}`;
  };

  const formatFileSize = (bytes) => {
    const fileBytes = Number(bytes);
    if (!Number.isFinite(fileBytes)) return '-';
    if (fileBytes < 1024) return `${fileBytes} B`;

    const units = ['KB', 'MB', 'GB'];
    let size = fileBytes / 1024;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
  };

  const formatDocumentDate = (documentFile) => {
    const rawDate = documentFile.updated_at || documentFile.created_at || documentFile.metadata?.lastModified;
    if (!rawDate) return '-';

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentsTotalSize = (documents) => documents.reduce((total, documentFile) => (
    total + Number(documentFile.metadata?.size || 0)
  ), 0);

  const listStorageDocuments = async (folderPath) => {
    const documents = [];

    const walkFolder = async (currentPath) => {
      const { data, error } = await supabase.storage
        .from('reports')
        .list(currentPath, {
          limit: 1000,
          sortBy: { column: 'name', order: 'asc' }
        });

      if (error) throw error;

      for (const item of data || []) {
        const itemPath = `${currentPath}/${item.name}`;
        const metadata = item.metadata || {};
        const isFile = Boolean(
          item.id ||
          metadata.mimetype ||
          metadata.lastModified ||
          metadata.size !== undefined
        );

        if (isFile) {
          documents.push({
            ...item,
            path: itemPath,
            displayName: itemPath.replace(`${folderPath}/`, '')
          });
        } else {
          await walkFolder(itemPath);
        }
      }
    };

    await walkFolder(folderPath);
    return documents;
  };

  // Fetches the AVV and RAW documents for a given model report, storing them in state. If the report has no storage path, it clears the document lists. Errors are caught and stored in state for display.
  const fetchEditDocuments = async (report) => {
    setLoadingEditDocuments(true);
    setEditDocumentError(null);
    setEditDocuments({ avv: [], mr: [] });

    if (!report?.storage_path) {
      setLoadingEditDocuments(false);
      return;
    }

    try {
      const [avvDocuments, mrDocuments] = await Promise.all([
        listStorageDocuments(`${report.storage_path}/AVV DOCUMENTS`),
        listStorageDocuments(`${report.storage_path}/RAW DOCUMENTS`)
      ]);

      setEditDocuments({
        avv: avvDocuments,
        mr: mrDocuments
      });
    } catch (error) {
      console.error('Error fetching model report documents:', error);
      setEditDocumentError(error.message || 'Failed to load documents for this model report.');
    } finally {
      setLoadingEditDocuments(false);
    }
  };

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);
  // Build a map of report IDs to the full report row
  const reportsMap = {};
  reports.forEach(r => {
    reportsMap[r.id] = r; // include any fields needed, e.g., type_price, sevs, etc.
  });

  const buildMreSelectionPayload = (report) => ({
    mreId: report.id,
    mreDetails: {
      mrApproval: report.mr_approval,
      make: report.make,
      model: report.model,
      chassisCode: report.chassis_code,
      startDate: report.start_date,
      endDate: report.end_date
    }
  });

  const handleSelect = (report) => {
    const payload = buildMreSelectionPayload(report);

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'MRE_SELECTED',
        ...payload
      }, '*');
      window.close();
      return;
    }

    navigate('/new-order', { state: payload });
  };

  // If the SEVS expiry is expired, we set a pending selection and wait for confirmation. Otherwise, we proceed with the selection.
  const handleSelectClick = (report) => {
    if (isExpiredSevs(report.sevs_expiry)) {
      setPendingSelection(report);
      return;
    }

    handleSelect(report);
  };

  const handleConfirmSelection = () => {
    if (!pendingSelection) return;

    handleSelect(pendingSelection);
    setPendingSelection(null);
  };

  const handleCancelSelection = () => {
    setPendingSelection(null);
  };

  const openEditModal = (report) => {
    const nextFormData = getEditFormDataFromReport(report);

    setEditReport(report);
    setEditFormData(nextFormData);
    setEditError(null);
    setEditFieldErrors({});
    setEditConfirmation(null);
    setSendingVariationNotification(false);
    setVariationReminderOpen(false);
    setVariationConfirmationOpen(false);
    fetchEditDocuments(report);
  };

  const closeEditModal = () => {
    if (savingEdit || sendingVariationNotification) return;

    setEditReport(null);
    setEditFormData({});
    setEditError(null);
    setEditFieldErrors({});
    setSendingVariationNotification(false);
    setVariationReminderOpen(false);
    setVariationConfirmationOpen(false);
    setEditDocuments({ avv: [], mr: [] });
    setEditDocumentError(null);
    setDeletingAllDocumentType(null);
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (editFieldErrors[name]) {
      setEditFieldErrors(prev => {
        const nextErrors = { ...prev };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  /**
   * Saves the changes made in the edit form to the Supabase database. Validates the form data before sending the update request. If successful, updates the local state with the new report data and optionally closes the edit modal. Displays error messages if validation fails or if the update request encounters an error.
   */
  const saveEditChanges = async ({ closeOnSuccess = true, confirmation } = {}) => {
    if (!editReport) return;

    const { isValid, errors: fieldErrors } = validateEditForm();
    if (!isValid) {
      setEditError('Please fill in all required fields.');
      scrollToFirstEditFieldError(fieldErrors);
      return null;
    }

    // Price is a numeric column, so a cleared input must become null rather than ''.
    const vehiclePrice = editFormData.vehicle_price === '' ? null : Number(editFormData.vehicle_price);

    // Build the payload for the update request, ensuring that empty strings are converted to null and that the vehicle price is correctly handled.
    const payload = editableModelReportFields.reduce((values, field) => {
      if (field.name === 'vehicle_price') {
        return { ...values, vehicle_price: vehiclePrice };
      }

      const value = editFormData[field.name];
      return {
        ...values,
        [field.name]: typeof value === 'string' && value.trim() === '' ? null : value
      };
    }, {
      is_available_to_buy: editFormData.is_available_to_buy !== false,
      updated_at: new Date().toISOString()
    });

    setSavingEdit(true);
    setEditError(null);

    try {
      const { data, error } = await supabase
        .from('customer_model_reports')
        .update(payload)
        .eq('id', editReport.id)
        .select('*, id')
        .single();

      if (error) throw error;

      // Update the local state with the new report data, either from the response or by merging the payload with the existing report data. This ensures that the UI reflects the latest changes without needing to refetch all reports.
      const updatedReport = data || { ...editReport, ...payload };
      setReports(prevReports => prevReports.map(report => (
        report.id === updatedReport.id ? updatedReport : report
      )));
      setEditReport(closeOnSuccess ? null : updatedReport);
      setEditFormData(closeOnSuccess ? {} : getEditFormDataFromReport(updatedReport));
      if (confirmation !== null) {
        setEditConfirmation(confirmation || {
          title: 'Changes saved',
          message: `${updatedReport.mr_approval || 'Model report'} has been updated successfully.`
        });
      }

      return updatedReport;
    } catch (error) {
      console.error('Error updating model report:', error);
      setEditError(error.message || 'Failed to update model report.');
      return null;
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    await saveEditChanges();
  };

  /**
   * Asks the send-bulk-email edge function to notify every customer who ordered this report that
   * its details have changed. The description is mandatory because it forms the email body.
   */
  const sendVariationNotification = async (reportId = editReport?.id) => {
    if (!reportId) {
      setEditError('No model report selected for variation notification.');
      return false;
    }

    const trimmedVariationDescription = variationDescription.trim();
    if (!trimmedVariationDescription) {
      setEditError('Variation description is required.');
      return false;
    }

    setSendingVariationNotification(true);
    setEditError(null);
    setEditConfirmation(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-bulk-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'variation-notification',
            reportId,
            variationDescription: trimmedVariationDescription,
          }),
        }
      );

      if (!response.ok) {
        const responseText = await response.text();
        let errorMessage = responseText;

        try {
          const errorBody = JSON.parse(responseText);
          errorMessage = errorBody.details || errorBody.error || responseText;
        } catch {
          errorMessage = responseText;
        }

        throw new Error(errorMessage || 'Failed to send variation notification.');
      }

      setEditConfirmation({
        title: 'Variation notification sent',
        message: 'The variation notification has been sent to all customers.',
      });
      return true;
    } catch (error) {
      setEditError(error.message || 'Failed to send variation notification.');
      return false;
    } finally {
      setSendingVariationNotification(false);
    }
  };

  const openVariationConfirmation = () => {
    if (!editReport) return;

    setEditError(null);
    setVariationReminderOpen(false);
    setVariationConfirmationOpen(true);
    setVariationDescription('');
  };

  const handleVariationConfirmationCancel = () => {
    if (sendingVariationNotification) return;

    setVariationConfirmationOpen(false);
  };

  const handleVariationConfirmationSend = async () => {
    if (!editReport) return;

    const success = await sendVariationNotification(editReport.id);
    if (success) {
      setVariationConfirmationOpen(false);
    }
  };

  const handleSendVariationNotificationClick = async () => {
    if (hasUnsavedEditChanges()) {
      setVariationReminderOpen(true);
      return;
    }

    openVariationConfirmation();
  };

  /**
   * Saves the current edit changes without displaying a success dialog, and then proceeds directly to the variation notification preview.
   */
  const handleVariationReminderSave = async () => {
    const updatedReport = await saveEditChanges({
      closeOnSuccess: false,
      confirmation: null
    });

    if (updatedReport) {
      setVariationReminderOpen(false);
      setVariationConfirmationOpen(true);
      setVariationDescription('');
    }
  };

  /**
   * Discards form edits back to the last saved row so the admin can send without saving.
   */
  const handleVariationReminderRevert = () => {
    if (!editReport) return;

    setEditFormData(getEditFormDataFromReport(editReport));
    setEditError(null);
    setEditFieldErrors({});
    setVariationReminderOpen(false);
  };

  // Uploads the selected files into this report's AVV or RAW folder, then reloads the listing.
  const handleDocumentUpload = async (e, type) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    if (!editReport?.storage_path) {
      setEditDocumentError('No storage path found for this model report.');
      return;
    }

    setUploadingDocumentType(type);
    setEditDocumentError(null);

    try {
      const folder = getDocumentFolder(type);
      const results = await Promise.all(selectedFiles.map(file => (
        supabase.storage
          .from('reports')
          .upload(`${editReport.storage_path}/${folder}/${sanitizeStorageFileName(file.name)}`, file, {
            cacheControl: '3600',
            upsert: true
          })
      )));
      const uploadError = results.find(result => result.error)?.error;
      if (uploadError) throw uploadError;

      await fetchEditDocuments(editReport);
    } catch (error) {
      console.error('Error uploading model report document:', error);
      setEditDocumentError(error.message || 'Failed to upload document.');
    } finally {
      setUploadingDocumentType(null);
      // Reset the file input so re-picking the same file still fires a change event.
      if (type === 'avv' && avvDocumentInputRef.current) {
        avvDocumentInputRef.current.value = '';
      }
      if (type === 'mr' && mrDocumentInputRef.current) {
        mrDocumentInputRef.current.value = '';
      }
    }
  };

  const handleDocumentView = async (documentFile) => {
    setEditDocumentError(null);

    try {
      const { data, error } = await supabase.storage
        .from('reports')
        // The reports bucket is private, so viewing needs a short-lived (60s) signed URL.
        .createSignedUrl(documentFile.path, 60);

      if (error) throw error;

      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening model report document:', error);
      setEditDocumentError(error.message || 'Failed to open document.');
    }
  };

  const handleDocumentDownload = async (documentFile) => {
    setEditDocumentError(null);

    try {
      const { data, error } = await supabase.storage
        .from('reports')
        .download(documentFile.path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentFile.name;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading model report document:', error);
      setEditDocumentError(error.message || 'Failed to download document.');
    }
  };

  // Permanently removes a document from storage; there is no undo, hence the confirm prompt.
  const handleDocumentDelete = async (documentFile, type) => {
    if (!window.confirm(`Delete ${documentFile.name}?`)) return;

    setDeletingDocumentPath(documentFile.path);
    setEditDocumentError(null);

    try {
      const { error } = await supabase.storage
        .from('reports')
        .remove([documentFile.path]);

      if (error) throw error;

      await fetchEditDocuments(editReport);
    } catch (error) {
      console.error('Error deleting model report document:', error);
      setEditDocumentError(error.message || 'Failed to delete document.');
    } finally {
      setDeletingDocumentPath(null);
    }
  };

  // Permanently removes every document in the AVV or RAW folder for this report.
  const handleDocumentDeleteAll = async (type) => {
    const documents = editDocuments[type] || [];
    if (documents.length === 0) return;

    const label = type === 'avv' ? 'AVV' : 'RAW';
    if (!window.confirm(`Delete all ${documents.length} ${label} document${documents.length === 1 ? '' : 's'}? This cannot be undone.`)) {
      return;
    }

    setDeletingAllDocumentType(type);
    setEditDocumentError(null);

    try {
      const paths = documents.map((documentFile) => documentFile.path);
      const { error } = await supabase.storage
        .from('reports')
        .remove(paths);

      if (error) throw error;

      await fetchEditDocuments(editReport);
    } catch (error) {
      console.error('Error deleting all model report documents:', error);
      setEditDocumentError(error.message || `Failed to delete all ${label} documents.`);
    } finally {
      setDeletingAllDocumentType(null);
    }
  };

  const renderEditField = (field) => {
    const fieldError = editFieldErrors[field.name];

    if (field.type === 'select') {
      return (
        <div key={field.name}>
          <label className={`mb-1 block text-xs font-medium ${fieldError ? 'text-red-700' : 'text-gray-700'} md:text-sm`}>
            {field.label}
          </label>
          <select
            name={field.name}
            value={editFormData[field.name] ?? ''}
            onChange={handleEditFieldChange}
            className={getEditFieldInputClassName(field.name)}
          >
            <option value="">Select type</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {fieldError && (
            <p className="mt-1 text-xs text-red-600 md:text-sm">{fieldError}</p>
          )}
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="sm:col-span-2">
          <label className={`mb-1 block text-xs font-medium ${fieldError ? 'text-red-700' : 'text-gray-700'} md:text-sm`}>
            {field.label}
          </label>
          <textarea
            name={field.name}
            value={editFormData[field.name] ?? ''}
            onChange={handleEditFieldChange}
            rows={3}
            className={getEditFieldInputClassName(field.name, { isTextarea: true })}
          />
          {fieldError && (
            <p className="mt-1 text-xs text-red-600 md:text-sm">{fieldError}</p>
          )}
        </div>
      );
    }

    return (
      <div key={field.name}>
        <label className={`mb-1 block text-xs font-medium ${fieldError ? 'text-red-700' : 'text-gray-700'} md:text-sm`}>
          {field.label}
        </label>
        <input
          type={field.type || 'text'}
          name={field.name}
          value={editFormData[field.name] ?? ''}
          onChange={handleEditFieldChange}
          step={field.type === 'number' ? '0.01' : undefined}
          className={getEditFieldInputClassName(field.name)}
        />
        {fieldError && (
          <p className="mt-1 text-xs text-red-600 md:text-sm">{fieldError}</p>
        )}
      </div>
    );
  };

  const renderDocumentSection = ({ type, title, description, documents }) => {
    const inputRef = type === 'avv' ? avvDocumentInputRef : mrDocumentInputRef;
    const isAvv = type === 'avv';
    const accentClasses = isAvv
      ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 focus:ring-blue-500'
      : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 focus:ring-blue-500';
    const iconClasses = isAvv ? 'text-blue-600' : 'text-blue-600';
    const uploading = uploadingDocumentType === type;
    const deletingAll = deletingAllDocumentType === type;
    const hasDocuments = documents.length > 0;

    return (
      <section className="overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <DocumentTextIcon className={`mt-0.5 h-5 w-5 ${iconClasses}`} />
            <div className="min-w-0">
              <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">{title}</h3>
              <p className="mt-1 hidden text-xs text-slate-500 md:block">{description}</p>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={(event) => handleDocumentUpload(event, type)}
              className="hidden"
            />
            {hasDocuments && (
              <button
                type="button"
                onClick={() => handleDocumentDeleteAll(type)}
                disabled={deletingAll || uploading || loadingEditDocuments || !!deletingDocumentPath}
                aria-label={deletingAll ? 'Deleting all documents' : `Delete all ${isAvv ? 'AVV' : 'RAW'} documents`}
                title={deletingAll ? 'Deleting...' : `Delete All ${isAvv ? 'AVV' : 'RAW'}`}
                className="inline-flex items-center rounded-[0.8rem] border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700 shadow-sm transition-all duration-200 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-[426px]:px-3 min-[426px]:py-2 md:px-3.5 md:py-2.5"
              >
                <TrashIcon className="h-4 w-4 min-[426px]:mr-1.5" />
                <span className="hidden min-[426px]:inline md:hidden">{deletingAll ? 'Deleting...' : 'Delete All'}</span>
                <span className="hidden md:inline">
                  {deletingAll ? 'Deleting...' : `Delete All ${isAvv ? 'AVV' : 'RAW'}`}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading || deletingAll || !editReport?.storage_path}
              aria-label={uploading ? 'Uploading document' : `Add ${isAvv ? 'AVV' : 'RAW'} document`}
              title={uploading ? 'Uploading...' : `Add ${isAvv ? 'AVV' : 'RAW'} Document`}
              className={`inline-flex items-center rounded-[0.8rem] border p-2 text-xs font-semibold shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-[426px]:px-3 min-[426px]:py-2 md:px-3.5 md:py-2.5 ${accentClasses}`}
            >
              <PlusIcon className="h-4 w-4 min-[426px]:mr-1.5" />
              <span className="hidden min-[426px]:inline md:hidden">{uploading ? 'Adding...' : 'Add'}</span>
              <span className="hidden md:inline">{uploading ? 'Uploading...' : `Add ${isAvv ? 'AVV' : 'MR'} Document`}</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-xs md:text-sm">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="w-16 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">S No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">File Name</th>
                <th className="hidden w-48 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 sm:table-cell">Uploaded Date</th>
                <th className="w-36 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loadingEditDocuments ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                    Loading documents...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                    No documents found.
                  </td>
                </tr>
              ) : documents.map((documentFile, index) => (
                <tr key={documentFile.path}>
                  <td className="px-4 py-3 text-gray-700">{index + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
                      <button
                        type="button"
                        onClick={() => handleDocumentView(documentFile)}
                        className="truncate text-left text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline md:text-sm"
                        title={documentFile.displayName || documentFile.name}
                      >
                        {documentFile.displayName || documentFile.name}
                      </button>
                      <span className="flex-shrink-0 text-xs text-gray-500">
                        ({formatFileSize(documentFile.metadata?.size)})
                      </span>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-xs text-gray-600 sm:table-cell md:text-sm">{formatDocumentDate(documentFile)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDocumentView(documentFile)}
                        className="rounded-md border border-blue-200 bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        aria-label={`View ${documentFile.name}`}
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDocumentDownload(documentFile)}
                        className="rounded-md border border-green-200 bg-green-50 p-2 text-green-600 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                        aria-label={`Download ${documentFile.name}`}
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDocumentDelete(documentFile, type)}
                        disabled={deletingDocumentPath === documentFile.path || deletingAll}
                        className="rounded-md border border-red-100 bg-red-50 p-2 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Delete ${documentFile.name}`}
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-200 bg-slate-50/80 px-5 py-3 text-xs text-slate-600">
          <span>Total Files: <span className="font-semibold text-slate-800">{documents.length}</span></span>
          <span>Total Size: <span className="font-semibold text-slate-800">{formatFileSize(getDocumentsTotalSize(documents))}</span></span>
        </div>
      </section>
    );
  };


  const PaginationControls = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }




    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstReport + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(indexOfLastReport, filteredReports.length)}
              </span> of{' '}
              <span className="font-medium">{filteredReports.length}</span> results
            </p>
          </div>
          <div>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-l-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>

              {startPage > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    1
                  </button>
                  {startPage > 2 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">
                      ...
                    </span>
                  )}
                </>
              )}

              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => setCurrentPage(number)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${currentPage === number
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {number}
                </button>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">
                      ...
                    </span>
                  )}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-r-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  // The same table is wrapped in the admin or customer chrome depending on who is viewing it.
  const Layout = isManagementListView ? AdminLayout : CustomerLayout;

  return (
    <Layout
      activeItemId="model-report-list"
      title="Model Report List"
      subtitle={isManagementListView
        ? 'View or manage model reports, edit records, and submit variations.'
        : isPopupSelectionMode
          ? 'Select a model report for your new order.'
          : 'Browse available model reports and select one to place a new order.'}
      contentClassName="w-full max-w-none"
      footerContainerClassName="mt-10 lg:mt-14"
      showSidebar={!isPopupSelectionMode}
      hideCart={isPopupSelectionMode}
    >
      {pendingSelection && createPortal(
        <div className="modal-backdrop-animate fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="modal-panel-animate w-full max-w-[21rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] sm:max-w-[25rem]">
            <div className="px-5 pt-5">
              <div className="inline-flex items-center rounded-[0.55rem] bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-red-700">
                Warning
              </div>
              <div className="mt-3 flex items-start gap-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-red-100 ring-1 ring-red-200">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-sm font-medium uppercase tracking-[0.04em] text-gray-950">Expired SEVS</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    This has an expired SEVS. Do you still want to buy this?
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-5 py-3.5">
              <button
                type="button"
                onClick={handleCancelSelection}
                className="inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSelection}
                className="inline-flex min-w-[96px] items-center justify-center rounded-[0.65rem] bg-red-600 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {editReport && createPortal(
        <div className="modal-backdrop-animate fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="modal-panel-animate flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h2 className="text-md font-medium text-gray-900 uppercase tracking-[0.04em]">
                  <span className="lg:hidden">Edit MRE</span>
                  <span className="hidden lg:inline">Edit Model Report</span>
                </h2>
                <p className="mt-1 hidden text-sm text-slate-500 sm:block">
                  {getModelReportDisplayName(editReport)}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {isAdminListView && (
                  <label className="inline-flex items-center gap-2 rounded-[999px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-700 shadow-sm sm:gap-3 sm:px-3.5 sm:py-2.5 sm:text-sm">
                    <span className="hidden font-medium sm:inline lg:hidden">Avail. to buy</span>
                    <span className="hidden lg:inline font-medium">Available to buy</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={editFormData.is_available_to_buy !== false}
                      title={editFormData.is_available_to_buy !== false ? 'Available to buy' : 'Unavailable to buy'}
                      onClick={() => setEditFormData(prev => ({
                        ...prev,
                        is_available_to_buy: prev.is_available_to_buy === false
                      }))}
                      disabled={savingEdit || sendingVariationNotification}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:h-6 sm:w-11 ${editFormData.is_available_to_buy !== false ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform sm:h-5 sm:w-5 ${editFormData.is_available_to_buy !== false ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0.5 sm:translate-x-1'}`}
                      />
                    </button>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => {
                    openVariationHistory(editReport.id);
                    closeEditModal();
                  }}
                  disabled={savingEdit || sendingVariationNotification}
                  title="View variation history"
                  aria-label="View variation history"
                  className="inline-flex h-9 items-center justify-center rounded-[0.7rem] border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10 sm:rounded-[0.8rem] sm:px-3.5 sm:text-sm"
                >
                  <ClockIcon className="h-3.5 w-3.5 shrink-0 text-gray-500 sm:h-4 sm:w-4 lg:mr-1.5" aria-hidden="true" />
                  <span className="hidden lg:inline">View history</span>
                </button>
                <button
                  type="button"
                  onClick={handleSendVariationNotificationClick}
                  disabled={savingEdit || sendingVariationNotification}
                  title="Send variation notification"
                  aria-label="Send variation notification"
                  className="inline-flex h-9 items-center justify-center rounded-[0.7rem] border border-blue-200 bg-blue-50 px-2.5 text-xs font-semibold text-blue-600 shadow-sm transition-all duration-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10 sm:rounded-[0.8rem] sm:px-3.5 sm:text-sm"
                >
                  <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 lg:mr-1.5" aria-hidden="true" />
                  <span className="hidden lg:inline">
                    {sendingVariationNotification ? 'Sending...' : 'Send Variation Notification'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={savingEdit || sendingVariationNotification}
                  className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Close edit modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveEdit} className="flex min-h-0 flex-1 flex-col">
              <div className="space-y-5 overflow-y-auto bg-slate-50/60 px-6 py-5">
                {editError && (
                  <div className="mb-4 rounded-[0.9rem] border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 shadow-sm md:text-sm">
                    {editError}
                  </div>
                )}
                {editDocumentError && (
                    <div className="rounded-[0.9rem] border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 shadow-sm md:text-sm">
                    {editDocumentError}
                  </div>
                )}

                <section className="overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
                  <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
                    <DocumentTextIcon className="mt-0.5 h-5 w-5 text-blue-600" />
                    <div>
                      <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Details</h3>
                      <p className="mt-1 hidden text-xs text-slate-500 md:block">Manage MRE details related to this Model Report.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
                    {editableModelReportFields.map(renderEditField)}
                  </div>
                </section>

                {renderDocumentSection({
                  type: 'avv',
                  title: 'AVV Documents',
                  description: 'Manage AVV documents related to this Model Report.',
                  documents: editDocuments.avv
                })}

                {renderDocumentSection({
                  type: 'mr',
                  title: 'RAW Documents',
                  description: 'Manage RAW documents related to this Model Report.',
                  documents: editDocuments.mr
                })}
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={savingEdit || sendingVariationNotification}
                  className="rounded-[0.8rem] border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit || sendingVariationNotification}
                  className="inline-flex items-center rounded-[0.8rem] border border-transparent bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {variationReminderOpen && editReport && createPortal(
        <div className="modal-backdrop-animate fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="modal-panel-animate relative w-full max-w-[21rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] sm:max-w-[25rem]">
            <button
              type="button"
              onClick={() => setVariationReminderOpen(false)}
              disabled={savingEdit || sendingVariationNotification}
              className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Keep editing"
              title="Keep editing"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <div className="px-5 pt-5">
              <div className="inline-flex items-center rounded-[0.55rem] bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-orange-700">
                Attention
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-3 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-200">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div className="pt-2.5">
                  <h2 className="text-sm font-medium text-gray-950 uppercase tracking-[0.04em]">Unsaved Changes</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Save your changes before sending the variation notification, or revert to the last saved details.
                  </p>
                </div>
              </div>

              {editError && (
                <div className="mt-4 rounded-[0.75rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {editError}
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-5 py-3.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setVariationReminderOpen(false)}
                disabled={savingEdit || sendingVariationNotification}
                className="inline-flex min-w-[96px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={handleVariationReminderRevert}
                disabled={savingEdit || sendingVariationNotification}
                className="inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Revert
              </button>
              <button
                type="button"
                onClick={handleVariationReminderSave}
                disabled={savingEdit || sendingVariationNotification}
                className="inline-flex min-w-[108px] items-center justify-center rounded-[0.65rem] bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sendingVariationNotification ? 'Sending...' : savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {variationConfirmationOpen && editReport && createPortal(
        <div className="modal-backdrop-animate fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="modal-panel-animate relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]">
            <div className="flex items-start justify-between gap-4 px-5 pt-5 flex-none">
              <div className="flex items-start gap-4">
                <div className="mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 ring-1 ring-sky-200">
                  <EnvelopeIcon className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <h2 className="mt-2.5 text-md font-medium text-gray-950 uppercase tracking-[0.04em]">Variation Notification Preview</h2>
                  <p className="mt-2 hidden text-sm leading-6 text-slate-500 min-[475px]:block">Review the details below before notifying the customer.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleVariationConfirmationCancel}
                disabled={sendingVariationNotification}
                className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close confirmation modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 pb-4 pt-4 overflow-y-auto flex-1">
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/70 p-3.5 shadow-sm">
                <table className="w-full text-sm table-fixed">
                  <tbody>
                    {getVariationNotificationPreview(editReport).map((item) => (
                      <tr key={item.label} className="align-top">
                        <td className="w-1/3 py-2 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{item.label}</td>
                        <td className="py-2 px-3 text-sm text-gray-900 break-words">{item.value || 'Not provided'}</td>
                      </tr>
                    ))}
                    <tr className="align-top">
                      <td className="w-1/3 py-2 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Variation Description <span className="text-red-500">*</span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <textarea
                          value={variationDescription}
                          onChange={(event) => setVariationDescription(event.target.value)}
                          rows={4}
                          required
                          placeholder="Enter the variation description"
                          className="block w-full rounded-[0.75rem] border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {editError && (
                <div className="mt-4 rounded-[0.75rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {editError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/70 px-5 py-3.5 flex-none">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleVariationConfirmationCancel}
                  disabled={sendingVariationNotification}
                  className="inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVariationConfirmationSend}
                  disabled={sendingVariationNotification || !variationDescription.trim()}
                  className="inline-flex min-w-[118px] items-center justify-center rounded-[0.65rem] bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendingVariationNotification ? 'Sending...' : 'Confirm & Send'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {editConfirmation && createPortal(
        <div className="modal-backdrop-animate fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="modal-panel-animate w-full max-w-[21rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] sm:max-w-[25rem]">
            <div className="px-4 pt-4 sm:px-5 sm:pt-5">
              <div className="inline-flex items-center rounded-[0.55rem] bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                Success
              </div>
              <h2 className="mt-2 text-sm font-medium uppercase tracking-[0.04em] text-gray-900 sm:mt-2.5 sm:text-md">{editConfirmation.title}</h2>
              <p className="mt-2 text-xs leading-5 text-slate-500 sm:text-sm sm:leading-6">
                {editConfirmation.message}
              </p>
            </div>
            <div className="mt-4 flex justify-end border-t border-slate-200 bg-slate-50/70 px-4 py-3 sm:mt-5 sm:px-5 sm:py-3.5">
              <button
                type="button"
                onClick={() => setEditConfirmation(null)}
                className="inline-flex min-w-[72px] items-center justify-center rounded-[0.6rem] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:min-w-[84px] sm:rounded-[0.65rem] sm:px-3.5 sm:py-2 sm:text-[13px]"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <div className="max-w-8xl mx-auto px-4 pb-8">
        <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className={`grid flex-1 gap-4 ${isManagementListView ? 'lg:grid-cols-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.8fr)]' : 'lg:grid-cols-2 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]'}`}>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={selectedField ? `Search by ${searchFields.find((field) => field.value === selectedField)?.label.toLowerCase()}...` : 'Select a field to search...'}
                  disabled={!selectedField}
                  className={`w-full rounded-2xl border px-11 py-3 text-sm shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 ${!selectedField ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500' : 'border-slate-300 bg-white text-slate-900'}`}
                />
                <svg
                  className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 ${!selectedField ? 'text-slate-300' : 'text-slate-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <select
                value={selectedField}
                onChange={(e) => {
                  setSelectedField(e.target.value);
                  setSearchTerm('');
                }}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Select Search Field</option>
                {searchFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>

                {/* Availability filter is admin-only; customers never see unavailable reports. */}
              {isManagementListView && (
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="all">All Reports</option>
                  <option value="unavailable">Unavailable only</option>
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto shadow-sm rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1.5 py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500 md:px-4 md:py-4 md:text-xs"></th>
                  <th className="px-1.5 py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500 md:px-4 md:py-4 md:text-xs">Make</th>
                  <th className="px-1.5 py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500 md:px-4 md:py-4 md:text-xs">Model</th>
                  {/* <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Body Type</th>*/}
                  {!hideSevsMreInMainTable && (
                    <th className="py-4 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SEVS Entry</th>
                  )}
                  {!hideSevsMreInMainTable && (
                    <th className="py-4 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRE Approval</th>
                  )}
                  {!hideTypeInMainTable && (
                    <th className="py-4 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  )}
                  {showPriceColumn && !isCompactAdminTable && (
                    <th className="py-4 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                  )}
                  {actionColumnVisible && (
                    <th className="w-[4rem] px-1.5 py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500 md:w-[5.5rem] md:px-4 md:py-4 md:text-xs">
                      {isManagementListView ? 'Actions' : 'Action'}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentReports.map((report) => (

                  <React.Fragment key={report.id}>
                    {/* SEVS status tints the whole row: pink when expired, yellow when expiring. */}
                    {(() => {
                      const isExpired = isExpiredSevs(report.sevs_expiry);
                      const isExpiringSoon = isSevsExpiringSoon(report.sevs_expiry);
                      const rowStateClasses = isExpired
                        ? 'bg-[#FFE6EA]'
                        : isExpiringSoon
                          ? 'bg-[#FEFF8C]'
                          : 'text-gray-900';
                      const rowHoverClass = isExpired
                        ? 'bg-[#FFE6EA] hover:bg-[#FFE6EA] border-t-0'
                        : isExpiringSoon
                          ? 'bg-[#FEFF8C] hover:bg-[#FEFF8C] border-t-0'
                          : 'hover:bg-gray-50';

                      return (
                        <tr
                          ref={el => rowRefs.current[report.id] = el}
                          className={`cursor-pointer transition-colors duration-150 ${rowHoverClass}`}
                          onClick={() => toggleRow(report.id)}
                        >
                          <td className={`px-1.5 py-3 whitespace-nowrap md:px-6 md:py-5 ${isExpired ? 'bg-[#FFE6EA]' : isExpiringSoon ? 'bg-[#FEFF8C]' : ''}`}>
                            <button
                              type="button"
                              className="text-gray-500 focus:outline-none"
                              aria-label={expandedRows.has(report.id) ? 'Collapse details' : 'Expand details'}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleRow(report.id);
                              }}
                            >
                              {expandedRows.has(report.id) ? (
                                <ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
                              ) : (
                                <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                              )}
                            </button>
                          </td>
                          <td className={`px-1.5 py-3 text-[13px] leading-5 md:px-6 md:py-5 md:text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>{report.make || '-'}</td>
                          <td className={`px-1.5 py-3 text-[13px] leading-5 md:px-6 md:py-5 md:text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>{report.model || '-'}</td>
                          {/*  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{report.body_type || '-'}</td>*/}
                          {!hideSevsMreInMainTable && (
                            <td className={`px-6 py-5 text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>
                              <div className="flex flex-col gap-1">
                                <RoverSevLinks
                                  value={report.sevs_entry}
                                  roverUrls={report.sevs_entry_rover_url}
                                  className="whitespace-normal break-words text-inherit"
                                />
                                {isSevsUnderReview(report) && (
                                  <span className="inline-flex w-fit rounded-full border border-orange-500 bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-orange-600">
                                    Under review
                                  </span>
                                )}
                                {isExpired && (
                                  <span className="inline-flex w-fit rounded-full border border-red-200 bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#C41E3A]">
                                    Expired
                                  </span>
                                )}
                                {!isExpired && isExpiringSoon && (
                                  <span className="inline-flex w-fit rounded-full border border-[#FF8A00] bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#FF8A00]">
                                    {`Expiring- ${report.sevs_expiry || '-'}`}
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          {!hideSevsMreInMainTable && (
                            <td className={`px-6 py-5 whitespace-nowrap text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>
                              <div className="flex flex-col gap-1">
                                {report.mr_approval_rover_url ? (
                                  <RoverApprovalLink
                                    value={report.mr_approval}
                                    roverUrl={report.mr_approval_rover_url}
                                  />
                                ) : (
                                  <RoverApprovalText value={report.mr_approval} />
                                )}
                                {isManagementListView && !isAvailableToBuy(report) && (
                                  <span className="inline-flex w-fit items-center rounded-full border border-rose-300 bg-rose-100 px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.08em] text-rose-700 shadow-sm ring-1 ring-rose-200/70">
                                    Unavailable to buy
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          {!hideTypeInMainTable && (
                            <td className={`px-6 py-5 whitespace-normal break-words text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>{report.vehicle_type?.trim() || '-'}</td>
                          )}
                          {showPriceColumn && !isCompactAdminTable && (
                            <td className={`px-6 py-5 whitespace-nowrap text-sm ${isExpired || isExpiringSoon ? rowStateClasses : 'text-gray-900'}`}>
                              {isManagementListView ? getStoredPrice(report) : `$${getTotalPrice(report).toFixed(2)}`}
                            </td>
                          )}
                          {actionColumnVisible && (
                            <td className={`px-1.5 py-3 align-middle text-sm md:px-4 md:py-5 ${isExpired ? 'bg-[#FFE6EA]' : isExpiringSoon ? 'bg-[#FEFF8C]' : 'text-gray-900'}`}>
                              {isManagementListView ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    title="Variation notification history"
                                    aria-label="Variation notification history"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openVariationHistory(report.id);
                                    }}
                                    className="box-border inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 md:h-9 md:w-9"
                                  >
                                    <ClockIcon className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Edit model report"
                                    aria-label="Edit model report"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditModal(report);
                                    }}
                                    className="box-border inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 md:h-9 md:w-9"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectClick(report);
                                  }}
                                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                                >
                                  Select
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })()}
                    {expandedRows.has(report.id) && (
                      <tr>
                        <td colSpan={expandedDetailsColSpan} className="overflow-hidden px-4 py-4 bg-gray-50 sm:px-6">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            {hideTypeInMainTable && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">
                                  MRE Details
                                </h4>
                                {hideSevsMreInMainTable && (
                                  <div className="mb-1 flex flex-wrap items-center gap-x-1 gap-y-1">
                                    <span className="text-[13px] text-gray-700 md:text-sm">MRE Approval:</span>
                                    {report.mr_approval_rover_url ? (
                                      <RoverApprovalLink
                                        value={report.mr_approval}
                                        roverUrl={report.mr_approval_rover_url}
                                        className="text-[13px] md:text-sm"
                                        compact
                                      />
                                    ) : (
                                      <RoverApprovalText value={report.mr_approval} className="text-[13px] text-gray-600 md:text-sm" />
                                    )}
                                    {isManagementListView && !isAvailableToBuy(report) && (
                                      <span className="inline-flex w-fit items-center rounded-full border border-rose-300 bg-rose-100 px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.08em] text-rose-700 shadow-sm ring-1 ring-rose-200/70">
                                        Unavailable to buy
                                      </span>
                                    )}
                                  </div>
                                )}
                                <p className="text-[13px] text-gray-600 md:text-sm">Type: {report.vehicle_type?.trim() || '-'}</p>
                                {isValidValue(report.mr_description) && (
                                  <>
                                    <p className="text-[13px] text-gray-700 md:text-sm">Description:</p>
                                    <p className="text-[13px] text-gray-600 md:text-sm">{report.mr_description}</p>
                                  </>
                                )}
                              </div>
                            )}
                            {(isValidValue(report.engine_option_1) || isValidValue(report.engine_option_2)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Engine Details</h4>
                                {isValidValue(report.engine_option_1) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Option 1: {report.engine_option_1}</p>
                                )}
                                {isValidValue(report.engine_option_2) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Option 2: {report.engine_option_2}</p>
                                )}
                              </div>
                            )}

                            {(isValidValue(report.transmission) || isValidValue(report.drivetrain)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Transmission & Drivetrain</h4>
                                {isValidValue(report.transmission) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Transmission: {report.transmission}</p>
                                )}
                                {isValidValue(report.drivetrain) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Drivetrain: {report.drivetrain}</p>
                                )}
                              </div>
                            )}

                            {(isValidValue(report.seats_option_1) || isValidValue(report.seats_option_2) || isValidValue(report.seat_option_3)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Seating Options</h4>
                                {isValidValue(report.seats_option_1) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Option 1: {report.seats_option_1}</p>
                                )}
                                {isValidValue(report.seats_option_2) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Option 2: {report.seats_option_2}</p>
                                )}
                                {isValidValue(report.seat_option_3) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Option 3: {report.seat_option_3}</p>
                                )}
                              </div>
                            )}

                            {(isValidValue(report.start_date) || isValidValue(report.end_date)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Dates</h4>
                                {isValidValue(report.start_date) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Start: {report.start_date}</p>
                                )}
                                {isValidValue(report.end_date) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">End: {report.end_date}</p>
                                )}
                              </div>
                            )}

                            {(isValidValue(report.mr_wi_reference) || isValidValue(report.mr_vc_reference)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">References</h4>
                                {isValidValue(report.mr_wi_reference) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">WI Ref: {report.mr_wi_reference}</p>
                                )}
                                {isValidValue(report.mr_vc_reference) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">VC Ref: {report.mr_vc_reference}</p>
                                )}
                              </div>
                            )}

                            <div className="min-w-0 break-words overflow-wrap-anywhere">
                              <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">SEVS Details</h4>
                              {hideSevsMreInMainTable && (
                                <div className="mb-1 flex flex-wrap items-start gap-x-1 gap-y-1">
                                  <span className="text-[13px] text-gray-700 md:text-sm">SEVS Entry:</span>
                                  <RoverSevLinks
                                    value={report.sevs_entry}
                                    roverUrls={report.sevs_entry_rover_url}
                                    className="whitespace-normal break-words text-[13px] text-gray-600 md:text-sm"
                                    compact
                                  />
                                  {isSevsUnderReview(report) && (
                                    <span className="inline-flex w-fit rounded-full border border-orange-500 bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-orange-600">
                                      Under review
                                    </span>
                                  )}
                                  {isExpiredSevs(report.sevs_expiry) && (
                                    <span className="inline-flex w-fit rounded-full border border-red-200 bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#C41E3A]">
                                      Expired
                                    </span>
                                  )}
                                  {!isExpiredSevs(report.sevs_expiry) && isSevsExpiringSoon(report.sevs_expiry) && (
                                    <span className="inline-flex w-fit rounded-full border border-[#FF8A00] bg-[#FFFFF2] px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[#FF8A00]">
                                      {`Expiring- ${report.sevs_expiry || '-'}`}
                                    </span>
                                  )}
                                </div>
                              )}
                              {(() => {
                                let expired = false;
                                let expiryText = report.sevs_expiry || '-';
                                if (report.sevs_expiry) {
                                  expired = isExpiredSevs(report.sevs_expiry);
                                }
                                return (
                                  <p className="text-[13px] text-gray-600 md:text-sm">
                                    SEVS Expiry:{' '}
                                    <span className={expired ? "text-red-600 font-semibold" : ""}>
                                      {expiryText}
                                      {expired && !hideSevsMreInMainTable && <span className="ml-2">(expired)</span>}
                                    </span>
                                  </p>
                                );
                              })()}
                            </div>

                            {(isValidValue(report.origin_requirements) || isValidValue(report.cats_or_km_required) || isValidValue(report.camper_conversion_required) || isValidValue(report.welcab_type)) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">Requirements</h4>
                                {isValidValue(report.origin_requirements) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Origin: {report.origin_requirements}</p>
                                )}
                                {isValidValue(report.chassis_code) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Chassis Code: {report.chassis_code}</p>
                                )}
                                {isValidValue(report.body_type) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Body Type: {report.body_type}</p>
                                )}
                                {isValidValue(report.cats_or_km_required) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Cats or KM Required: {report.cats_or_km_required}</p>
                                )}
                                {isValidValue(report.camper_conversion_required) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Camper Conversion: {report.camper_conversion_required}</p>
                                )}
                                {isValidValue(report.welcab_type) && (
                                  <p className="text-[13px] text-gray-600 md:text-sm">Welcab Type: {report.welcab_type}</p>
                                )}
                              </div>
                            )}
                            {!hideTypeInMainTable && isValidValue(report.mr_description) && (
                              <div className="min-w-0 break-words overflow-wrap-anywhere">
                                <h4 className="text-[13px] font-medium uppercase tracking-[0.04em] text-gray-900 md:text-sm">MRE Details</h4>
                                <p className="text-[13px] text-gray-700 md:text-sm">Description:</p>
                                <p className="text-[13px] text-gray-600 md:text-sm">{report.mr_description}</p>
                              </div>
                            )}
                            {isCompactAdminTable && showPriceColumn && (
                              <div className="inline-flex max-w-full flex-col self-start rounded-[1rem] border border-blue-200 bg-blue-50/80 px-4 py-3 shadow-sm shadow-blue-100/60">
                                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-900">
                                  PRICE : <span className="text-blue-700 normal-case tracking-normal">{isManagementListView ? getStoredPrice(report) : `$${getTotalPrice(report).toFixed(2)}`}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls />
        </div>
      </div>

    </Layout>
  );
};

export default ModelReportDatabase;